import { HttpErrorResponse } from '@angular/common/http'
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	InputSignal,
	OnDestroy,
	output,
	OutputEmitterRef,
	Signal,
	signal,
	WritableSignal,
	inject,
} from '@angular/core'
import { OrderSideEnum } from '@modules/orders-module/enums/order-side.enum'
import { OrderTypeEnum } from '@modules/orders-module/enums/order-type.enum'
import { TitleOrderTermPipe } from '@modules/orders-module/pipes/title-order-term.pipe'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { TuiIcon } from '@taiga-ui/core'
import { Subject, takeUntil } from 'rxjs'
import { DatePipe, UpperCasePipe } from '@angular/common'
import { OptionTypeEnum } from '@modules/options-module/general/enums/option-type.enum'
import { OptionOrderInterface } from '@modules/options-module/orders/interfaces/option-order.interface'
import { OptionsOrdersHelperService } from '@modules/options-module/orders/services/options-orders-helper.service'
import { OptionOrderStateEnum } from '@modules/options-module/orders/enums/option-order-state.enum'
import { OptionsOrdersService } from '@modules/options-module/orders/services/options-orders.service'
import { OptionSharesPerContractConstant } from '@modules/options-module/general/constants/option-shares-per-contract.constant'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { PricePipe } from '@shared/pipes/price.pipe'
import { AmountPipe } from '@shared/pipes/amount.pipe'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { AlertService } from '@shared/services/alert.service'
import { StocksOrdersHelperService } from '@modules/stocks-module/orders/services/stocks-orders-helper.service'
import { ButtonComponent } from '@shared/components/ui-kit/button/button.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-cancel-options-order',
	templateUrl: './cancel-options-order.component.html',
	styleUrl: './cancel-options-order.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TranslatePipe,
		TuiIcon,
		HighlightDirective,
		TitleOrderTermPipe,
		DatePipe,
		UpperCasePipe,
		PricePipe,
		AmountPipe,
		QuantityPipe,
		ButtonComponent,
	],
})
export class CancelOptionsOrderComponent implements OnDestroy {
	protected readonly orderTypeEnum: typeof OrderTypeEnum = OrderTypeEnum
	protected readonly orderSideEnum: typeof OrderSideEnum = OrderSideEnum
	protected readonly optionTypeEnum: typeof OptionTypeEnum = OptionTypeEnum
	protected readonly optionOrderStateEnum: typeof OptionOrderStateEnum = OptionOrderStateEnum
	protected readonly optionSharesPerContractConstant: number = OptionSharesPerContractConstant

	public order: InputSignal<OptionOrderInterface | null> = input<OptionOrderInterface | null>(null)
	public closeDropdown: OutputEmitterRef<void> = output<void>()

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _optionsOrderService: OptionsOrdersService = inject<OptionsOrdersService>(OptionsOrdersService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _ordersStocksHelperService: StocksOrdersHelperService =
		inject<StocksOrdersHelperService>(StocksOrdersHelperService)
	private readonly _optionsOrderHelperService: OptionsOrdersHelperService =
		inject<OptionsOrdersHelperService>(OptionsOrdersHelperService)

	protected isLoading: WritableSignal<boolean> = signal<boolean>(false)

	protected totalAmount: Signal<number> = computed((): number => {
		const order: OptionOrderInterface | null = this.order()
		if (!order) return 0

		if (order.type === this.orderTypeEnum.LIMIT)
			return order.qty * order.limit_price * this.optionSharesPerContractConstant

		return order.qty * order.price * this.optionSharesPerContractConstant
	})

	protected isLimitPriceVisible: Signal<boolean> = computed((): boolean => {
		const order: OptionOrderInterface | null = this.order()
		if (!order) return false

		return this._ordersStocksHelperService.checkIsLimitPriceVisible(order.type, order?.order_class)
	})
	protected cancelOrder(): void {
		const order: OptionOrderInterface | null = this.order()
		if (!order) return

		this.isLoading.set(true)

		this._optionsOrderService
			.cancelOptionOrder(order.id)
			.pipe(takeUntil(this._destroy$))
			.subscribe(
				(): void => {
					if (!this.order) return
					this._optionsOrderHelperService.removeOptionOrder(order, this.optionOrderStateEnum.OPEN)
					this._alertService.success(this._translateService.instant('cancelOrder.orderSuccessfullyCanceled'))
					this.isLoading.set(false)
					this.onCloseDropdown()
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error?.message ||
							this._translateService.instant("cancelOrder.couldn'tCancelOrder"),
					)
					this.isLoading.set(false)
				},
			)
	}

	protected onCloseDropdown(): void {
		this.closeDropdown.emit()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
